/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {startTransition, useEffect, useState} from 'react'
import PropTypes from 'prop-types'

import {Link as RouteLink} from 'react-router-dom'
import Link from '../link'
import {
    Box,
    Text,
    Grid,
    Flex,
    Stack,
    Image,
    Heading,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import CloudinaryImage from '../cloudinary-media/image'
import {
    AccordionPanelSkeleton,
    NestedAccordionPanelSkeleton
} from './partials/accordion-panel-skeleton'
import {KS_BRAND_ID, YR_BRAND_ID} from '../../constants'
import {getUrl} from '../../utils/cloudinary-utils'
import useTagManager from '../../hooks/use-tag-manager'
import {categoryUrlBuilder} from '../../utils/url'
import useMonetate from '../../hooks/use-monetate'
import {useSiteContext} from '../../contexts/site'
import useNavigationHandler, {getNavigationProps} from '../../hooks/use-navigation-handler'
import {scheduleIdleAnalytics, scheduleAfterPaint} from '../../utils/common-utils'

export {NestedAccordionPanelSkeleton}

const DeferredAccordionPanelContent = ({children, fallback = null, shouldRender}) => {
    const [isReady, setIsReady] = useState(shouldRender)

    useEffect(() => {
        let isCancelled = false
        let deferredId

        if (!shouldRender) {
            setIsReady(false)
            return undefined
        }

        if (isReady) {
            return undefined
        }

        deferredId = scheduleAfterPaint(() => {
            if (!isCancelled) {
                startTransition(() => {
                    setIsReady(true)
                })
            }
        })

        return () => {
            isCancelled = true

            if (typeof deferredId !== 'number' || typeof window === 'undefined') {
                return
            }

            if (typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(deferredId)
            } else {
                window.clearTimeout(deferredId)
            }
        }
    }, [isReady, shouldRender])

    if (!isReady) {
        return fallback
    }

    return children
}

DeferredAccordionPanelContent.propTypes = {
    children: PropTypes.node,
    fallback: PropTypes.node,
    shouldRender: PropTypes.bool.isRequired
}

/**
 * The nested accordion allows you to create, as the name suggest, a nests
 * accordion given a hierarchical data structure. Each nests accordion will
 * be indented to further enhance the hierary view.
 */
const NestedAccordion = (props) => {
    const {isYR} = useSiteContext()
    const styles = useStyleConfig('NestedAccordion')
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const handleNavigation = useNavigationHandler()
    const {
        item,
        initialDepth = 0,
        itemsFilter = () => true,
        itemsAfter,
        itemsBefore,
        itemsKey = 'items',
        itemsCountKey = 'count',
        selectedBrand,
        children,
        itemComponent,
        setShowSpinner,
        ...rest
    } = props
    if (!item) {
        return <></>
    } // Adding this to cater the edge case.
    const depth = initialDepth
    const items = item[itemsKey] || []

    const filter = (item) =>
        typeof itemsFilter === 'function' ? itemsFilter(item) : !!item[itemsFilter]

    const ItemComponent = itemComponent || NestedAccordion
    const subCategoryView = item?.c_imageCardsLayoutInMobile || false

    const currentDate = new Date()
    const shouldParentAppear =
        (!item?.c_showAsImageInMobile && !item?.c_imageCardsLayoutInMobile) ||
        (item?.c_imageDisplayStartDate &&
            item?.c_imageDisplayEndDate &&
            new Date(item?.c_imageDisplayStartDate) < currentDate &&
            currentDate < new Date(item?.c_imageDisplayEndDate)) ||
        (item?.c_imageDisplayStartDate &&
            !item?.c_imageDisplayEndDate &&
            new Date(item?.c_imageDisplayStartDate) < currentDate)
    const categoryImageLayout =
        subCategoryView === 'grid' || subCategoryView === 'carousel' ? 'grid' : 'fullSize'
    const categoryItems =
        selectedBrand === KS_BRAND_ID ? items?.filter((item) => item?.ld !== YR_BRAND_ID) : items
    let defaultItems = []
    let imageItems = []
    categoryItems.forEach((item) => {
        const shouldShowAsImage =
            item?.c_showAsImageInMobile &&
            ['grid', 'carousel', 'single-image'].includes(subCategoryView)

        if (!shouldShowAsImage) {
            defaultItems.push(item)
            return
        }

        if (subCategoryView === 'single-image' && imageItems.length > 0) {
            defaultItems.push(item)
            return
        }
        const isValid =
            item?.c_showAsImageInMobile &&
            item?.c_imageDisplayStartDate &&
            new Date(item?.c_imageDisplayStartDate) < currentDate &&
            ((item?.c_imageDisplayEndDate && currentDate < new Date(item?.c_imageDisplayEndDate)) ||
                !item?.c_imageDisplayEndDate)
        if (isValid) {
            // If one image is expired, please don't include it again.
            imageItems.push(item)
        }
    })
    const allItems = [
        {type: 'images', items: imageItems},
        {type: 'default', items: defaultItems}
    ]

    // Accordion props conditionally set based on depth
    const accordionProps =
        depth === 1
            ? {
                  defaultIndex: Array.from({length: categoryItems?.length}, (_, i) => i),
                  allowMultiple: true
              }
            : {}

    const collapseProps =
        depth === 0
            ? {
                  className: 'static-collapse'
              }
            : {}

    return (
        <Accordion
            className="sf-nested-accordion"
            data-depth={depth}
            {...rest}
            {...accordionProps}
            data-selected-brand={selectedBrand}
        >
            {/* Optional accordion items before others in items list.  */}
            {typeof itemsBefore === 'function' ? itemsBefore({item, depth}) : itemsBefore}
            <Stack gap="30px">
                {allItems?.map((collection, index) =>
                    collection?.items?.length > 0 ? (
                        <Box
                            key={index}
                            as={
                                collection.type === 'images' && subCategoryView === 'grid'
                                    ? Grid
                                    : collection.type === 'images' &&
                                      subCategoryView === 'single-image'
                                    ? Stack
                                    : collection.type === 'images' && subCategoryView === 'carousel'
                                    ? Flex
                                    : Box
                            }
                            gap="10px"
                            gridTemplateColumns="1fr 1fr"
                            maxWidth={
                                collection.type === 'images' && subCategoryView === 'grid'
                                    ? '390px'
                                    : '100%'
                            }
                            overflowX={
                                collection.type === 'images' && subCategoryView === 'carousel'
                                    ? 'auto'
                                    : 'initial'
                            }
                            paddingX={depth === 2 ? '30px' : '0px'}
                            paddingBottom={
                                collection.type === 'images' && subCategoryView === 'carousel'
                                    ? '10px'
                                    : 'initial'
                            }
                            marginBottom={
                                collection.type === 'images' && subCategoryView === 'carousel'
                                    ? '-10px'
                                    : 'initial'
                            }
                            display={
                                shouldParentAppear
                                    ? ''
                                    : !(collection.type === 'images' && subCategoryView)
                                    ? ''
                                    : 'none'
                            }
                            data-start-schedule-parent={item?.c_imageDisplayStartDate}
                            data-expire-schedule-parent={item?.c_imageDisplayEndDate}
                            marginLeft={
                                collection.type === 'images' && subCategoryView === 'carousel'
                                    ? '30px'
                                    : 'unset'
                            }
                            marginRight={
                                collection.type === 'images' && subCategoryView === 'carousel'
                                    ? '0px'
                                    : 'initial'
                            }
                        >
                            {collection?.items.filter(filter).map((item) => {
                                const {
                                    id,
                                    name,
                                    c_badgeText,
                                    c_badgeTextColor,
                                    c_imageDisplayStartDate,
                                    c_imageDisplayEndDate,
                                    c_showAsImageInMobile,
                                    c_setShopAll,
                                    parentCategoryTree,
                                    c_hideOnMobile,
                                    c_alternativeUrl,
                                    c_sectionTypes,
                                    onlineSubCategoriesCount
                                } = item
                                const shouldChildAppear =
                                    c_showAsImageInMobile &&
                                    c_imageDisplayStartDate &&
                                    new Date(c_imageDisplayStartDate) < currentDate &&
                                    ((c_imageDisplayEndDate &&
                                        currentDate < new Date(c_imageDisplayEndDate)) ||
                                        !c_imageDisplayEndDate)
                                const itemsCount =
                                    item[itemsCountKey] || item[itemsKey]?.length || 0
                                const redirectionProps = !c_alternativeUrl
                                    ? {
                                          to: categoryUrlBuilder(item),
                                          as: RouteLink,
                                          isExternal: false
                                      }
                                    : {to: c_alternativeUrl, as: Link, isExternal: true}
                                const navigationProps = getNavigationProps(
                                    redirectionProps.to,
                                    redirectionProps.isExternal
                                )
                                return (
                                    <AccordionItem
                                        key={id}
                                        display={
                                            shouldChildAppear ||
                                            (!c_hideOnMobile && !item?.c_showAsImageInMobile)
                                                ? 'block'
                                                : 'none'
                                        }
                                        data-depth={depth}
                                        data-show-as-image-in-mobile-child={
                                            item?.c_showAsImageInMobile
                                        }
                                        data-hide-on-mobile-child={c_hideOnMobile}
                                        data-start-schedule-child={c_imageDisplayStartDate}
                                        data-expire-schedule-child={c_imageDisplayEndDate}
                                        {...styles.accordionItem}
                                        className={`acc-item-${c_hideOnMobile ? '-hide-cat-' : ''}${
                                            item?.c_showAsImageInMobile ? '-' : depth
                                        } cat-${id} ${
                                            onlineSubCategoriesCount
                                                ? 'parent-category'
                                                : 'child-category'
                                        }`}
                                        data-is-shop-all={c_setShopAll}
                                        data-id={id}
                                        id={`mobile-cat-${id}`}
                                    >
                                        {({isExpanded}) => (
                                            <Box
                                                className={`cat-item-${depth}-${
                                                    isExpanded ? 'expanded' : 'collapsed'
                                                }-${selectedBrand}`}
                                                data-content-schedule-start={
                                                    c_imageDisplayStartDate
                                                }
                                                data-content-schedule-expire={c_imageDisplayEndDate}
                                            >
                                                {isExpanded && depth === 0 && <>{children}</>}

                                                {/* Heading */}
                                                <Heading {...styles.heading}>
                                                    {/* Show item as a leaf node if it has no visible child items. */}
                                                    {itemsCount > 0 &&
                                                    !c_setShopAll &&
                                                    depth < 2 ? (
                                                        <AccordionButton
                                                            {...styles.internalButton.main}
                                                            width={
                                                                c_sectionTypes === 'dynamic'
                                                                    ? '100%'
                                                                    : 'fit-content'
                                                            }
                                                            marginLeft={depth ? 'initial' : '1rem'}
                                                            margin={
                                                                isExpanded && !depth ? 'auto' : ''
                                                            }
                                                            data-id={id}
                                                            onClickCapture={() => {
                                                                const shouldShowBackSelection =
                                                                    !isExpanded && !depth

                                                                scheduleAfterPaint(async () => {
                                                                    if (shouldShowBackSelection) {
                                                                        tagManager.navigationMain(
                                                                            'header',
                                                                            name.toLowerCase(),
                                                                            undefined,
                                                                            undefined
                                                                        )
                                                                    }
                                                                    if (props.onClick) {
                                                                        startTransition(() => {
                                                                            props.onClick(true)
                                                                        })
                                                                    }
                                                                    if (props.setBackSelection) {
                                                                        startTransition(() => {
                                                                            props.setBackSelection(
                                                                                shouldShowBackSelection
                                                                            )
                                                                        })
                                                                    }
                                                                })
                                                            }}
                                                            pointerEvents={
                                                                depth &&
                                                                c_sectionTypes !== 'dynamic'
                                                                    ? 'none'
                                                                    : 'unset'
                                                            }
                                                            padding={
                                                                isExpanded && !depth
                                                                    ? '2rem 0 1rem 0'
                                                                    : '1rem 0'
                                                            }
                                                        >
                                                            {isExpanded && !depth && (
                                                                <Image
                                                                    alt="Arrow"
                                                                    position="absolute"
                                                                    left="33px"
                                                                    width="15px"
                                                                    height="16px"
                                                                    src={getUrl(
                                                                        'UIImages/images/left-arrow-icon.svg'
                                                                    )}
                                                                    {...styles.internalButtonIcon}
                                                                />
                                                            )}
                                                            <Text
                                                                {...styles.internalButton.text}
                                                                paddingX={
                                                                    depth === 1 ? '30px' : '0px'
                                                                }
                                                                fontFamily={
                                                                    isExpanded && !depth
                                                                        ? 'body !important'
                                                                        : isYR
                                                                        ? 'caslon !important'
                                                                        : 'cormorant !important'
                                                                }
                                                                fontSize={
                                                                    isExpanded && !depth
                                                                        ? '16px !important'
                                                                        : '22px'
                                                                }
                                                                color={
                                                                    depth
                                                                        ? 'gray.800'
                                                                        : isExpanded && !depth
                                                                        ? 'black.100'
                                                                        : 'black'
                                                                }
                                                                fontWeight={
                                                                    isExpanded && !depth
                                                                        ? '300'
                                                                        : '400'
                                                                }
                                                                className={`category-name category-name-${item?.id}`}
                                                                data-depth={depth}
                                                            >
                                                                {name}
                                                            </Text>
                                                            {!isExpanded && !depth && (
                                                                <Image
                                                                    alt="Right chevron"
                                                                    {...styles.internalButtonIcon}
                                                                    width="10px"
                                                                    data-depth={depth}
                                                                    data-expanded={isExpanded}
                                                                    height="10px"
                                                                    src={getUrl(
                                                                        'UIImages/images/chevron-right-icon.svg'
                                                                    )}
                                                                />
                                                            )}

                                                            {c_sectionTypes === 'dynamic' &&
                                                                (isExpanded ? (
                                                                    <Image
                                                                        alt="Right chevron"
                                                                        {...styles.internalButtonIcon}
                                                                        width="10px"
                                                                        height="10px"
                                                                        marginRight="15px"
                                                                        src={getUrl(
                                                                            'UIImages/images/minus-large.svg'
                                                                        )}
                                                                    />
                                                                ) : (
                                                                    <Image
                                                                        alt="Right chevron"
                                                                        {...styles.internalButtonIcon}
                                                                        width="10px"
                                                                        height="10px"
                                                                        marginRight="15px"
                                                                        src={getUrl(
                                                                            'UIImages/images/plus-large.svg'
                                                                        )}
                                                                    />
                                                                ))}
                                                        </AccordionButton>
                                                    ) : (
                                                        <AccordionButton
                                                            {...styles.leafButton}
                                                            {...redirectionProps}
                                                            {...styles.internalButton.main}
                                                            marginLeft={
                                                                c_setShopAll ? 'auto' : 'unset'
                                                            }
                                                            display={
                                                                item?.c_showInMenu
                                                                    ? 'block'
                                                                    : 'none'
                                                            }
                                                            paddingY={
                                                                collection.type === 'images'
                                                                    ? '0px'
                                                                    : depth === 0
                                                                    ? '15px'
                                                                    : '10px'
                                                            }
                                                            paddingLeft={
                                                                depth >= 1 ? '0px' : '1rem'
                                                            }
                                                            onClick={(e) => {
                                                                handleNavigation(
                                                                    e,
                                                                    navigationProps,
                                                                    props.onClose
                                                                )

                                                                // Deferred to idle: Track analytics after browser is ready
                                                                scheduleIdleAnalytics(() => {
                                                                    if (!depth) {
                                                                        tagManager.navigationMain(
                                                                            'header',
                                                                            name.toLowerCase(),
                                                                            null,
                                                                            null
                                                                        )
                                                                        monetate.setCategoriesViewed(
                                                                            name
                                                                        )
                                                                        monetate.setNavigationClicks(
                                                                            name
                                                                        )
                                                                    } else {
                                                                        if (
                                                                            !item?.c_showAsImageInMobile ||
                                                                            e.target.tagName === 'P'
                                                                        ) {
                                                                            tagManager.navigationMain(
                                                                                'header',
                                                                                depth > 1
                                                                                    ? parentCategoryTree?.[
                                                                                          parentCategoryTree?.length -
                                                                                              3
                                                                                      ]?.name?.toLowerCase()
                                                                                    : parentCategoryTree?.[
                                                                                          parentCategoryTree?.length -
                                                                                              2
                                                                                      ]?.name?.toLowerCase(),
                                                                                name.toLowerCase(),
                                                                                depth > 1
                                                                                    ? parentCategoryTree?.[
                                                                                          parentCategoryTree?.length -
                                                                                              2
                                                                                      ]?.name?.toLowerCase()
                                                                                    : undefined
                                                                            )
                                                                            monetate.setCategoriesViewed(
                                                                                name
                                                                            )
                                                                            monetate.setNavigationClicks(
                                                                                name
                                                                            )
                                                                        } else {
                                                                            tagManager.navigationCategoryCard(
                                                                                parentCategoryTree?.[
                                                                                    parentCategoryTree?.length -
                                                                                        3
                                                                                ]?.name?.toLowerCase(),
                                                                                name.toLowerCase(),
                                                                                parentCategoryTree?.[
                                                                                    parentCategoryTree?.length -
                                                                                        2
                                                                                ]?.name?.toLowerCase()
                                                                            )
                                                                            monetate.setCategoriesViewed(
                                                                                name
                                                                            )
                                                                            monetate.setNavigationClicks(
                                                                                name
                                                                            )
                                                                        }
                                                                    }
                                                                })
                                                            }}
                                                            color="black.100"
                                                            _hover={{
                                                                color: !depth
                                                                    ? 'black.100'
                                                                    : c_setShopAll
                                                                    ? 'blue.700'
                                                                    : 'teal.400',
                                                                textDecoration: 'none'
                                                            }}
                                                            transition="color 0.3s ease"
                                                        >
                                                            {collection.type === 'images' &&
                                                            item?.c_showAsImageInMobile ? (
                                                                <Stack gap="5px">
                                                                    <CloudinaryImage
                                                                        src={
                                                                            item?.c_menuImageUrlInMobile
                                                                        }
                                                                        alt={
                                                                            item?.c_mobileMenuImageAltText ||
                                                                            item?.name
                                                                        }
                                                                        aspectRatio={
                                                                            categoryImageLayout ===
                                                                            'grid'
                                                                                ? '160:194'
                                                                                : '351:210'
                                                                        }
                                                                        imgSizes={{
                                                                            base:
                                                                                categoryImageLayout ===
                                                                                'grid'
                                                                                    ? 160
                                                                                    : 351
                                                                        }}
                                                                        sizes={[
                                                                            categoryImageLayout ===
                                                                            'grid'
                                                                                ? 160
                                                                                : 351
                                                                        ]}
                                                                        styles={{
                                                                            aspectRatio:
                                                                                categoryImageLayout ===
                                                                                'grid'
                                                                                    ? '160/194'
                                                                                    : '351/210',
                                                                            width:
                                                                                categoryImageLayout ===
                                                                                'grid'
                                                                                    ? '160px'
                                                                                    : '351px',
                                                                            height: 'auto',
                                                                            minWidth:
                                                                                categoryImageLayout ===
                                                                                'grid'
                                                                                    ? '160px'
                                                                                    : '100%',
                                                                            htmlWidth:
                                                                                categoryImageLayout ===
                                                                                'grid'
                                                                                    ? 160
                                                                                    : 351,
                                                                            htmlHeight:
                                                                                categoryImageLayout ===
                                                                                'grid'
                                                                                    ? 194
                                                                                    : 210,
                                                                            minHeight:
                                                                                categoryImageLayout ===
                                                                                'grid'
                                                                                    ? 194
                                                                                    : 210
                                                                        }}
                                                                    />
                                                                    <Text
                                                                        fontSize="13px"
                                                                        fontFamily="body"
                                                                        fontWeight="300"
                                                                        lineHeight="22px"
                                                                        letterSpacing="0.065px"
                                                                        className={`sub-category sub-category-${id}`}
                                                                        data-depth={depth}
                                                                    >
                                                                        {name}
                                                                    </Text>
                                                                </Stack>
                                                            ) : (
                                                                <Box
                                                                    fontSize={
                                                                        !depth
                                                                            ? '22px'
                                                                            : c_setShopAll
                                                                            ? '14px'
                                                                            : '16px'
                                                                    }
                                                                    fontFamily={
                                                                        !depth
                                                                            ? isYR
                                                                                ? 'caslon'
                                                                                : 'cormorant'
                                                                            : 'body'
                                                                    }
                                                                    textDecoration={
                                                                        c_setShopAll
                                                                            ? 'underline'
                                                                            : 'unset'
                                                                    }
                                                                    textUnderlineOffset="4px"
                                                                    {...styles.text}
                                                                    className={`sub-category sub-category-${id}`}
                                                                    data-depth={depth}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        flexDirection="column"
                                                                        alignItems="flex-start"
                                                                    >
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="flex-start"
                                                                            gap="1"
                                                                        >
                                                                            <Text
                                                                                fontSize={{
                                                                                    lg: '14px'
                                                                                }}
                                                                                lineHeight="22px"
                                                                                fontWeight="300"
                                                                            >
                                                                                {name}
                                                                            </Text>
                                                                            {item?.c_badgeText && (
                                                                                <Text
                                                                                    as="span"
                                                                                    fontSize="8px"
                                                                                    fontWeight="600"
                                                                                    color={
                                                                                        c_badgeTextColor ||
                                                                                        'black.100'
                                                                                    }
                                                                                    mt="1px"
                                                                                >
                                                                                    {c_badgeText}
                                                                                </Text>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </AccordionButton>
                                                    )}
                                                </Heading>
                                                {/* Child Items */}
                                                {(isExpanded || depth === 1) &&
                                                    item?.c_showInMenu && (
                                                        <AccordionPanel
                                                            {...styles.panel}
                                                            paddingRight="0"
                                                            className={`cat-acc-pannel cat-acc-pannel-${id}`}
                                                            motionProps={collapseProps}
                                                        >
                                                            <DeferredAccordionPanelContent
                                                                shouldRender={
                                                                    isExpanded || depth === 1
                                                                }
                                                                fallback={
                                                                    <AccordionPanelSkeleton
                                                                        initialDepth={depth + 1}
                                                                        item={item}
                                                                        itemsCountKey={
                                                                            itemsCountKey
                                                                        }
                                                                        styles={styles}
                                                                    />
                                                                }
                                                            >
                                                                <ItemComponent
                                                                    {...styles.nestedAccordion}
                                                                    onClick={props.onClick}
                                                                    item={item}
                                                                    itemsKey={itemsKey}
                                                                    onClose={props.onClose}
                                                                    itemsCountKey={itemsCountKey}
                                                                    setShowSpinner={setShowSpinner}
                                                                    initialDepth={depth + 1}
                                                                    itemComponent={NestedAccordion}
                                                                    isExpanded={
                                                                        isExpanded || depth === 1
                                                                    }
                                                                />
                                                            </DeferredAccordionPanelContent>
                                                        </AccordionPanel>
                                                    )}
                                            </Box>
                                        )}
                                    </AccordionItem>
                                )
                            })}
                        </Box>
                    ) : null
                )}
            </Stack>

            {/* Optional accordion items after others in items list.  */}
            {typeof itemsAfter === 'function' ? itemsAfter({item, depth}) : itemsAfter}
        </Accordion>
    )
}

NestedAccordion.displayName = 'NestedAccordion'

NestedAccordion.propTypes = {
    /**
     * A POJO consistening of an id, name, and items array of object with
     * similarly specified properties.
     */
    item: PropTypes.object.isRequired,
    /**
     * An array of `AccordionItem` components that will be displayed after all
     * of the child items. Alternatively you can pass a function that will recieve
     * the current item and it's depth, your should return an `AccordionItem` or
     * array of `AccordionItem`'s.
     */
    itemsAfter: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    /**
     * An array of `AccordionItem` components that will be displayed before all
     * of the child items. Alternatively you can pass a function that will recieve
     * the current item and it's depth, your should return an `AccordionItem` or
     * array of `AccordionItem`'s.
     */
    itemsBefore: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    /**
     * This is an internally used property used to pass the updated depth of the
     * child accordion. This is used to access specfic styl data for accodions
     * based on their depth.
     */
    initialDepth: PropTypes.number,
    /**
     * Component to be rendered in place of the inner nested accordion.
     */
    itemComponent: PropTypes.elementType,
    /**
     * By default child items are keyed at `items` but if your data differs you
     * can specify a custom key name for chile items. (e.g. children)
     */
    itemsKey: PropTypes.string,
    /**
     * This is the brand that user has selected in the drawer.
     */
    selectedBrand: PropTypes.string,
    /**
     * This property represents the item key that represents the count of sub-items. This is used
     * to display a leaf node of a sub nested accordion.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Programatically filter out items that you do not want to show. You can do this by
     * supplying a string that will be used to access an items value, the the value is truthy
     * the item will be displayed. Otherwise you can pass a function, this function will be passed
     * the item to be filtered, its return is expected to be true or false.
     */
    itemsFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    onClick: PropTypes.func,
    setBackSelection: PropTypes.func,
    setShowBackSection: PropTypes.func,
    setShowSpinner: PropTypes.func,
    onClose: PropTypes.func,
    children: PropTypes.node
}

export default NestedAccordion
